import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ChatButton } from "../components/ChatButton";
import { resourceApi } from "../utils/api-axios";

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [isContentVisible, setIsContentVisible] = useState(false);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const data = await resourceApi.getAllResources();
        if (data.success) {
          setResources(data.resources);
        }
      } catch (error) {
        console.error("Failed to fetch resources:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsContentVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const target = document.getElementById("resources-content");
    if (target) observer.observe(target);

    return () => observer.disconnect();
  }, []);

  const toggleExpand = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedId(expandedId === id ? null : id);
  };

  // Group resources into blocks of 3 — first is big (left), next two are small (right)
  const buildBlocks = (list) => {
    const blocks = [];
    for (let i = 0; i < list.length; i += 3) {
      blocks.push({
        big: list[i],
        smalls: [list[i + 1], list[i + 2]].filter(Boolean),
      });
    }
    return blocks;
  };

  // The big featured card — full excerpt visible
  const BigCard = ({ resource }) => {
    const isExpanded = expandedId === resource._id;
    const hasLongExcerpt =
      resource.excerpt && resource.excerpt.length > 220;
    const displayExcerpt =
      hasLongExcerpt && !isExpanded
        ? resource.excerpt.slice(0, 220) + "..."
        : resource.excerpt;

    return (
      <a
        href={resource.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
      >
        {resource.imageUrl && (
          <div className="w-full bg-muted flex items-center justify-center overflow-hidden aspect-[4/3]">
            <img
              src={resource.imageUrl}
              alt={resource.sourceName}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}

        <div className="p-6 md:p-8 flex flex-col flex-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
            {resource.sourceName}
          </div>

          {resource.title && (
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 leading-tight">
              {resource.title}
            </h2>
          )}

          <p className="text-foreground/80 leading-relaxed mb-4">
            {resource.blurb}
          </p>

          {resource.excerpt && (
            <blockquote className="border-l-4 border-primary/40 bg-primary/5 pl-4 pr-4 py-3 my-2 text-muted-foreground italic text-sm leading-relaxed rounded-r-md">
              {displayExcerpt}
              {hasLongExcerpt && (
                <button
                  onClick={(e) => toggleExpand(resource._id, e)}
                  className="not-italic font-semibold text-primary hover:text-primary/80 ml-2 underline"
                >
                  {isExpanded ? "show less" : "read more"}
                </button>
              )}
            </blockquote>
          )}

          <div className="mt-auto pt-4 flex items-center text-sm font-semibold text-primary group-hover:text-primary/80">
            Visit source
            <ExternalLink className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      </a>
    );
  };

  // Smaller side cards — horizontal layout (image left, text right), no excerpt
  const SmallCard = ({ resource }) => (
    <a
      href={resource.sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex"
    >
      {resource.imageUrl && (
        <div className="w-32 sm:w-40 flex-shrink-0 bg-muted overflow-hidden">
          <img
            src={resource.imageUrl}
            alt={resource.sourceName}
            className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}

      <div className="p-4 md:p-5 flex flex-col flex-1 min-w-0">
        <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-primary mb-1.5 truncate">
          {resource.sourceName}
        </div>

        {resource.title && (
          <h3 className="text-base sm:text-lg font-bold text-foreground mb-1.5 leading-tight line-clamp-2">
            {resource.title}
          </h3>
        )}

        <p className="text-foreground/70 text-sm leading-snug line-clamp-3">
          {resource.blurb}
        </p>

        <div className="mt-auto pt-2 flex items-center text-xs font-semibold text-primary group-hover:text-primary/80">
          Visit
          <ExternalLink className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </a>
  );

  const blocks = buildBlocks(resources);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <ChatButton />

      <main
        id="resources-content"
        className={`flex-1 py-20 transition-all duration-1000 ${isContentVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
          }`}
      >
        <div className="container px-4 mx-auto max-w-6xl">
          {/* Page Header */}
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Resources
            </h1>
            <p className="text-muted-foreground text-lg">
              A growing collection of voices, ideas, and projects that inspire
              the work we're doing.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20 text-muted-foreground">
              Loading resources...
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              No resources yet — check back soon.
            </div>
          ) : (
            <div className="space-y-12">
              {blocks.map((block, blockIndex) => (
                <div
                  key={blockIndex}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
                >
                  {/* Big card on the left */}
                  <div>
                    <BigCard resource={block.big} />
                  </div>

                  {/* Smalls stacked on the right */}
                  <div className="flex flex-col gap-6 lg:gap-8">
                    {block.smalls.map((small) => (
                      <SmallCard key={small._id} resource={small} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Resources;